import {
  ContentSystemTaskKind,
  EditMemberOnboardingContextFragment$data,
  EditMemberOnboardingContextFragment$key,
} from "@/admin/member-onboarding/__generated__/EditMemberOnboardingContextFragment.graphql"
import { GlobalID, NodeFromConnection } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { graphql, useFragment } from "react-relay"

type Item = NodeFromConnection<
  NonNullable<EditMemberOnboardingContextFragment$data["children"]>
>

interface ContextValue {
  editingUsageKey: Item | null
  selectItem(id: GlobalID): void
  onClose: VoidFunction
  items: Item[]
  activeItemIndex: number
  hasSlackConnection: boolean
  hasCustomProfileFields: boolean
  hasPublishedCustomProfileFieldItems: boolean
  publishedProfileFieldsConnectionId?: string
}

const Context = createContext<ContextValue | null>(null)

export function useMemberOnboardingEditContext() {
  return useContext(Context)
}

interface ProviderProps {
  children: React.ReactNode
  /** key for the onboarding item top-level module usage */
  contentKey?: EditMemberOnboardingContextFragment$key | null
}

export type EditMemberOnboardingSearchParams = {
  openKind?: ContentSystemTaskKind
}

export default function EditMemberOnboardingContextProvider({
  children,
  contentKey = null,
}: ProviderProps) {
  const [editingId, setEditingId] = useState<GlobalID | null>(null)
  const content = useFragment<EditMemberOnboardingContextFragment$key>(
    graphql`
      fragment EditMemberOnboardingContextFragment on Content {
        id
        organization {
          hasSlackConnection(version: 2)
          organizationMembershipSlackConnections {
            edges {
              node {
                isPrimary
              }
            }
          }
          profileFields(isDefault: false, status: visible) {
            totalCount
          }
        }
        children(includeDraft: true) {
          edges {
            node {
              id
              content {
                systemTaskKind
                publishedProfileFields: children(includeDraft: false) {
                  __id
                  totalCount
                }
              }
              ...AdminMemberOnboardingListItemFragment
              ...EditMemberOnboardingDrawerHeaderFragment
              ...EditMemberOnboardingStepSwitcherFragment
              ...EditMemberOnboardingFormFragment
            }
          }
        }
      }
    `,
    contentKey
  )
  const { slackV2 } = useFeatureFlags()

  const items = Relay.connectionToArray(content?.children).filter((item) => {
    let isIncluded = true
    if (!slackV2 && item.content.systemTaskKind === "slack_connection") isIncluded = false
    return isIncluded
  })

  const mappedItems = items.reduce((map, item) => {
    map[item.id] = item
    return map
  }, {} as Record<GlobalID, (typeof items)[number]>)

  const editingUsageKey = useMemo(() => {
    if (!editingId) return null
    return mappedItems[editingId]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId])

  // Open a specific item's settings if specified in query params
  const [queryParams, setQueryParams] =
    useQueryParamState<EditMemberOnboardingSearchParams>()
  useEffect(() => {
    if (!queryParams.openKind) return
    const item = items.find((i) => i.content.systemTaskKind === queryParams.openKind)
    if (item) selectItem(item.id)
    setQueryParams({ openKind: undefined }, "replace")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.openKind])

  if (!content || !items.length) return null
  const primarySlackAdmin =
    content?.organization.organizationMembershipSlackConnections.edges.find(
      ({ node }) => node.isPrimary
    )
  const customProfileFieldsItem = items.find(
    (item) => item.content.systemTaskKind === "custom_profile_field"
  )

  return (
    <Context.Provider
      value={{
        editingUsageKey,
        selectItem,
        onClose,
        items,
        activeItemIndex: items.findIndex((item) => item.id === editingId),
        hasSlackConnection: Boolean(
          content.organization.hasSlackConnection && primarySlackAdmin
        ),
        hasCustomProfileFields: content.organization.profileFields.totalCount > 0,
        hasPublishedCustomProfileFieldItems:
          (customProfileFieldsItem?.content.publishedProfileFields?.totalCount || 0) > 0,
        publishedProfileFieldsConnectionId:
          customProfileFieldsItem?.content.publishedProfileFields?.__id,
      }}
    >
      {children}
    </Context.Provider>
  )

  function selectItem(id: GlobalID) {
    setEditingId(id)
  }

  function onClose() {
    setEditingId(null)
  }
}
